import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '../../api/client.js';
import type { AdminApprovalAuthority, AdminTaskOrder } from '../../api/types.js';
import {
  Alert,
  Button,
  Card,
  Field,
  PageHeader,
  Select,
  Spinner,
  TextArea,
  TextInput,
} from '../../components/ui.js';
import { formatCurrency, formatDateTime } from '../../lib/format.js';

const ROLE_OPTIONS = [
  { value: 'customer_cor', label: "USPS Contracting Officer's Representative (COR)" },
  { value: 'customer_co', label: 'USPS Contracting Officer (CO)' },
];

/**
 * The dollar limits that decide which USPS authority may approve a proposal. They are
 * configuration because a contract modification changes them; a change supersedes the
 * previous limit rather than overwriting it, so approvals already given keep the
 * authority that applied at the time.
 */
export function AdminApprovalAuthorityTab() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    roleCode: 'customer_cor',
    taskOrderId: '',
    maxAmount: '',
    referralRequiredAbove: '',
    changeReason: '',
  });

  const authorities = useQuery({
    queryKey: ['admin', 'approval-authorities'],
    queryFn: () =>
      apiRequest<{ items: AdminApprovalAuthority[] }>('/api/admin/approval-authorities').then(
        (result) => result.items,
      ),
  });

  const taskOrders = useQuery({
    queryKey: ['admin', 'task-orders'],
    queryFn: () =>
      apiRequest<{ taskOrders: AdminTaskOrder[] }>('/api/admin/task-orders').then(
        (result) => result.taskOrders,
      ),
  });

  const save = useMutation({
    mutationFn: () =>
      apiRequest('/api/admin/approval-authorities', {
        method: 'PUT',
        body: {
          roleCode: form.roleCode,
          taskOrderId: form.taskOrderId || null,
          maxAmount: form.maxAmount.trim() || null,
          referralRequiredAbove: form.referralRequiredAbove.trim() || null,
          changeReason: form.changeReason.trim(),
        },
      }),
    onSuccess: () => {
      setForm({ ...form, maxAmount: '', referralRequiredAbove: '', changeReason: '' });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'approval-authorities'] });
    },
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'The approval limit could not be saved. Please try again.',
      );
    },
  });

  return (
    <>
      <PageHeader
        title="Approval authority"
        subtitle="Which USPS authority may approve a proposal, by value. Leave the limit blank for no ceiling. A change applies to proposals submitted afterwards; approvals already recorded keep the authority that applied when they were given."
      />

      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}

      <Card title="Limits in force" className="mb-4" bodyClassName="px-0 py-0 sm:px-0">
        {authorities.isLoading ? (
          <div className="p-4">
            <Spinner label="Loading approval limits" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <caption className="sr-only">Approval limits in force</caption>
              <thead className="bg-slate-50">
                <tr>
                  <th scope="col" className="table-header-cell">
                    Authority
                  </th>
                  <th scope="col" className="table-header-cell">
                    Task order
                  </th>
                  <th scope="col" className="table-header-cell text-right">
                    Approves up to
                  </th>
                  <th scope="col" className="table-header-cell text-right">
                    Response Line above
                  </th>
                  <th scope="col" className="table-header-cell">
                    In force since
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {authorities.data?.map((row) => (
                  <tr key={row.id}>
                    <td className="table-cell-base">{row.roleName}</td>
                    <td className="table-cell-base">{row.taskOrderNumber ?? 'All task orders'}</td>
                    <td className="table-cell-base text-right tabular-nums">
                      {row.maxAmountCents === null
                        ? 'No ceiling'
                        : formatCurrency(row.maxAmountCents)}
                    </td>
                    <td className="table-cell-base text-right tabular-nums">
                      {row.referralAboveCents === null
                        ? '—'
                        : formatCurrency(row.referralAboveCents)}
                    </td>
                    <td className="table-cell-base">{formatDateTime(row.effectiveFrom)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Change a limit">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Authority" htmlFor="authorityRole" required>
              <Select
                id="authorityRole"
                value={form.roleCode}
                onChange={(event) => setForm({ ...form, roleCode: event.target.value })}
              >
                {ROLE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Task order"
              htmlFor="authorityTaskOrder"
              hint="Leave as all task orders unless this limit is negotiated differently for one."
            >
              <Select
                id="authorityTaskOrder"
                value={form.taskOrderId}
                onChange={(event) => setForm({ ...form, taskOrderId: event.target.value })}
              >
                <option value="">All task orders</option>
                {taskOrders.data?.map((taskOrder) => (
                  <option key={taskOrder.id} value={taskOrder.id}>
                    {taskOrder.number}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Approves up to"
              htmlFor="authorityMax"
              hint="Dollars. Blank means no ceiling."
            >
              <TextInput
                id="authorityMax"
                inputMode="decimal"
                value={form.maxAmount}
                onChange={(event) => setForm({ ...form, maxAmount: event.target.value })}
              />
            </Field>
            <Field
              label="Response Line required above"
              htmlFor="authorityReferral"
              hint="Dollars. Above this, USPS must open a Facilities Response Line ticket."
            >
              <TextInput
                id="authorityReferral"
                inputMode="decimal"
                value={form.referralRequiredAbove}
                onChange={(event) =>
                  setForm({ ...form, referralRequiredAbove: event.target.value })
                }
              />
            </Field>
          </div>
          <Field
            label="Reason for the change"
            htmlFor="authorityReason"
            required
            hint="Recorded in the audit trail, e.g. the contract modification number."
          >
            <TextArea
              id="authorityReason"
              rows={2}
              value={form.changeReason}
              onChange={(event) => setForm({ ...form, changeReason: event.target.value })}
            />
          </Field>
          <div>
            <Button
              onClick={() => {
                setError(null);
                save.mutate();
              }}
              disabled={form.changeReason.trim().length < 3}
              loading={save.isPending}
            >
              Save limit
            </Button>
          </div>
        </div>
      </Card>
    </>
  );
}
